/* Bronson collection grid: sidebar + drawer filters, AJAX updates via the Section Rendering API, price range, card swatches */
if (!customElements.get('bronson-collection-grid')) {
  customElements.define(
    'bronson-collection-grid',
    class BronsonCollectionGrid extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.openState = new Map();
        this.swatchObservers = [];

        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
        this.onPopState = this.onPopState.bind(this);

        // the drawer lives on <body> so it sits above the sticky header
        this.drawer = this.querySelector('[data-bcg-drawer]');
        if (this.drawer) {
          this.drawer.setAttribute('style', this.getAttribute('style') || '');
          document.body.appendChild(this.drawer);
        }

        this.roots().forEach((root) => {
          root.addEventListener('click', this.onClick);
          root.addEventListener('change', this.onChange);
          root.addEventListener('input', this.onInput);
          root.addEventListener('submit', this.onSubmit);
        });
        document.addEventListener('keyup', this.onKeyup);
        window.addEventListener('popstate', this.onPopState);

        this.bar = this.querySelector('[data-bcg-bar]');
        if (this.bar && this.classList.contains('bcg--sticky-bar') && 'ResizeObserver' in window) {
          this.barObserver = new ResizeObserver(() => this.style.setProperty('--bcg-bar-h', `${this.bar.offsetHeight}px`));
          this.barObserver.observe(this.bar);
        }

        this.init();
      }

      disconnectedCallback() {
        this.roots().forEach((root) => {
          root.removeEventListener('click', this.onClick);
          root.removeEventListener('change', this.onChange);
          root.removeEventListener('input', this.onInput);
          root.removeEventListener('submit', this.onSubmit);
        });
        document.removeEventListener('keyup', this.onKeyup);
        window.removeEventListener('popstate', this.onPopState);
        this.barObserver?.disconnect();
        this.swatchObservers.forEach((o) => o.disconnect());
        this.controller?.abort();
        document.documentElement.classList.remove('bcg-lock');
        this.drawer?.remove();
      }

      roots() {
        return [this, this.drawer].filter(Boolean);
      }

      forms() {
        return this.roots().flatMap((root) => [...root.querySelectorAll('[data-bcg-form]')]);
      }

      init() {
        this.syncSidebar();
        this.forms().forEach((form) => form.querySelectorAll('[data-bcg-price]').forEach((price) => this.updateSlider(price)));
        this.initSwatches();
      }

      /* ----- events ----- */
      onClick(e) {
        const target = e.target;

        if (target.closest('[data-bcg-toggle-sidebar]')) {
          this.classList.toggle('is-sidebar-hidden');
          this.syncSidebar();
          return;
        }

        const opener = target.closest('[data-bcg-open-drawer]');
        if (opener) {
          this.openDrawer(opener);
          return;
        }

        if (target.closest('[data-bcg-close-drawer]')) {
          this.closeDrawer();
          return;
        }

        const toggle = target.closest('[data-bcg-group-toggle]');
        if (toggle) {
          this.toggleGroup(toggle);
          return;
        }

        const link = target.closest('a[data-bcg-link]');
        if (link && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
          e.preventDefault();
          this.render(link.href);
        }
      }

      onChange(e) {
        const input = e.target;
        const form = input.closest('[data-bcg-form]');
        if (!form) return;
        if (input.type === 'checkbox' || input.type === 'range' || input.matches('[data-bcg-price-min], [data-bcg-price-max]')) {
          clearTimeout(this.priceTimer);
          this.submit(form);
        }
      }

      onInput(e) {
        const input = e.target;
        const price = input.closest('[data-bcg-price]');
        const form = input.closest('[data-bcg-form]');
        if (!price || !form) return;

        const max = parseFloat(price.dataset.max) || 0;
        const minRange = price.querySelector('[data-bcg-range-min]');
        const maxRange = price.querySelector('[data-bcg-range-max]');
        const minInput = price.querySelector('[data-bcg-price-min]');
        const maxInput = price.querySelector('[data-bcg-price-max]');

        if (input === minRange || input === maxRange) {
          if (parseFloat(minRange.value) > parseFloat(maxRange.value)) {
            if (input === minRange) minRange.value = maxRange.value;
            else maxRange.value = minRange.value;
          }
          minInput.value = parseFloat(minRange.value) > 0 ? minRange.value : '';
          maxInput.value = parseFloat(maxRange.value) < max ? maxRange.value : '';
          this.updateSlider(price);
          return;
        }

        if (minRange && maxRange) {
          minRange.value = minInput.value || 0;
          maxRange.value = maxInput.value || max;
          this.updateSlider(price);
        }
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.submit(form), 800);
      }

      onSubmit(e) {
        const form = e.target.closest('[data-bcg-form]');
        if (!form) return;
        e.preventDefault();
        clearTimeout(this.priceTimer);
        this.submit(form);
      }

      onKeyup(e) {
        if (e.key === 'Escape') this.closeDrawer();
      }

      onPopState() {
        this.render(window.location.href, false);
      }

      /* ----- sidebar + groups ----- */
      syncSidebar() {
        const hidden = this.classList.contains('is-sidebar-hidden');
        this.querySelectorAll('[data-bcg-toggle-sidebar]').forEach((button) => {
          button.setAttribute('aria-expanded', String(!hidden));
          const label = button.querySelector('[data-bcg-sidebar-label]');
          if (label) label.textContent = hidden ? label.dataset.closed : label.dataset.open;
        });
      }

      toggleGroup(toggle) {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));

        const group = toggle.closest('[data-bcg-group]');
        const form = toggle.closest('[data-bcg-form]');
        if (group && form) this.openState.set(`${form.dataset.location}:${group.dataset.bcgGroup}`, open);

        if (!panel.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          panel.hidden = !open;
          return;
        }

        panel.getAnimations().forEach((animation) => animation.cancel());
        const timing = { duration: 400, easing: 'cubic-bezier(0.19, 1, 0.22, 1)' };
        if (open) {
          panel.hidden = false;
          panel.animate([{ height: '0px' }, { height: `${panel.scrollHeight}px` }], timing);
        } else {
          const animation = panel.animate([{ height: `${panel.scrollHeight}px` }, { height: '0px' }], timing);
          animation.onfinish = () => {
            if (toggle.getAttribute('aria-expanded') === 'false') panel.hidden = true;
          };
        }
      }

      restoreGroups() {
        this.forms().forEach((form) =>
          form.querySelectorAll('[data-bcg-group]').forEach((group) => {
            const key = `${form.dataset.location}:${group.dataset.bcgGroup}`;
            if (!this.openState.has(key)) return;
            const open = this.openState.get(key);
            const toggle = group.querySelector('[data-bcg-group-toggle]');
            const panel = group.querySelector('[data-bcg-group-panel]');
            toggle?.setAttribute('aria-expanded', String(open));
            if (panel) panel.hidden = !open;
          })
        );
      }

      /* ----- price slider ----- */
      updateSlider(price) {
        const slider = price.querySelector('[data-bcg-slider]');
        const minRange = price.querySelector('[data-bcg-range-min]');
        const maxRange = price.querySelector('[data-bcg-range-max]');
        if (!slider || !minRange || !maxRange) return;
        const max = parseFloat(price.dataset.max) || 1;
        const from = Math.min(100, Math.max(0, ((parseFloat(minRange.value) || 0) / max) * 100));
        const to = Math.min(100, Math.max(0, ((parseFloat(maxRange.value) || max) / max) * 100));
        slider.style.setProperty('--from', `${from}%`);
        slider.style.setProperty('--to', `${to}%`);
        // keep the lower handle reachable when both handles meet at the end
        minRange.style.zIndex = from > 90 ? '3' : '';
      }

      /* ----- drawer ----- */
      openDrawer(trigger) {
        if (!this.drawer) return;
        this.lastTrigger = trigger;
        this.drawer.hidden = false;
        document.documentElement.classList.add('bcg-lock');
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            this.drawer.classList.add('is-open');
            this.drawer.querySelector('.bcg-drawer__close')?.focus({ preventScroll: true });
          })
        );
      }

      closeDrawer() {
        if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
        this.drawer.classList.remove('is-open');
        document.documentElement.classList.remove('bcg-lock');
        clearTimeout(this.drawerTimer);
        this.drawerTimer = setTimeout(() => {
          if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
        }, 500);
        let trigger = this.lastTrigger;
        if (!trigger?.isConnected) trigger = [...this.querySelectorAll('[data-bcg-open-drawer]')].find((el) => el.offsetParent);
        trigger?.focus({ preventScroll: true });
      }

      /* ----- filtering ----- */
      submit(form) {
        const params = new URLSearchParams();
        for (const [key, value] of new FormData(form)) {
          if (String(value).trim() !== '') params.append(key, value);
        }

        form.querySelectorAll('[data-bcg-price]').forEach((price) => {
          const max = parseFloat(price.dataset.max);
          const minInput = price.querySelector('[data-bcg-price-min]');
          const maxInput = price.querySelector('[data-bcg-price-max]');
          if (minInput && params.has(minInput.name) && parseFloat(params.get(minInput.name)) <= 0) params.delete(minInput.name);
          if (maxInput && params.has(maxInput.name) && parseFloat(params.get(maxInput.name)) >= max) params.delete(maxInput.name);
        });

        const query = params.toString();
        this.render(`${window.location.pathname}${query ? `?${query}` : ''}`);
      }

      async render(url, push = true) {
        const target = new URL(url, window.location.href);
        const request = new URL(target.href);
        request.searchParams.set('section_id', this.sectionId);

        this.controller?.abort();
        this.controller = new AbortController();
        this.classList.add('is-loading');

        try {
          const response = await fetch(request.href, { signal: this.controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          this.update(new DOMParser().parseFromString(html, 'text/html'));
          if (push) window.history.pushState({ bcg: this.sectionId }, '', `${target.pathname}${target.search}`);
        } catch (error) {
          if (error.name === 'AbortError') return;
          window.location.href = target.href;
          return;
        }

        this.classList.remove('is-loading');
        this.scrollToResults();
      }

      update(doc) {
        const fresh = doc.querySelector('bronson-collection-grid');
        if (!fresh) throw new Error('Section markup missing');
        const freshDrawer = fresh.querySelector('[data-bcg-drawer]');
        const activeId = document.activeElement?.id;

        const swap = (root, source) => {
          if (!root || !source) return;
          root.querySelectorAll('[data-bcg-replace]').forEach((el) => {
            const next = source.querySelector(`[data-bcg-replace="${el.dataset.bcgReplace}"]`);
            if (next) el.replaceWith(document.importNode(next, true));
          });
        };
        // replace the drawer first so its nodes are not matched while swapping the section
        swap(this.drawer, freshDrawer);
        freshDrawer?.remove();
        swap(this, fresh);

        this.restoreGroups();
        this.init();

        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      }

      scrollToResults() {
        const main = this.querySelector('.bcg__main');
        if (!main) return;
        const offset = (parseFloat(getComputedStyle(this).getPropertyValue('--bcg-sticky-top')) || 0) + (this.bar?.offsetHeight || 0);
        const top = main.getBoundingClientRect().top + window.scrollY - offset;
        if (window.scrollY > top + 1) window.scrollTo({ top, behavior: 'smooth' });
      }

      /* ----- card swatches (overflow arrows + image preview) ----- */
      initSwatches() {
        this.swatchObservers.forEach((o) => o.disconnect());
        this.swatchObservers = [];
        this.querySelectorAll('[data-swatches]').forEach((wrap) => {
          const list = wrap.querySelector('[data-swatch-list]');
          const prev = wrap.querySelector('[data-swatch-prev]');
          const next = wrap.querySelector('[data-swatch-next]');
          if (!list) return;

          const check = () => {
            list.style.maxWidth = '';
            const first = list.querySelector('.bfc-card__swatch');
            if (first && list.scrollWidth > list.clientWidth + 1) {
              const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
              const size = first.offsetWidth;
              const navSpace = (prev?.offsetWidth || 16) + (next?.offsetWidth || 16) + 2 * (parseFloat(getComputedStyle(wrap).columnGap) || 0);
              const available = wrap.clientWidth - navSpace;
              const count = Math.max(1, Math.floor((available + gap) / (size + gap)));
              list.style.maxWidth = `${count * size + (count - 1) * gap + 2}px`;
            }
            const overflow = list.scrollWidth > list.clientWidth + 1;
            if (prev) {
              prev.hidden = !overflow;
              prev.disabled = list.scrollLeft <= 1;
            }
            if (next) {
              next.hidden = !overflow;
              next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
            }
          };

          if (!wrap.dataset.bound) {
            wrap.dataset.bound = 'true';
            list.addEventListener('scroll', check, { passive: true });
            prev?.addEventListener('click', () => list.scrollBy({ left: -48 }));
            next?.addEventListener('click', () => list.scrollBy({ left: 48 }));

            const img = wrap.closest('[data-card]')?.querySelector('[data-primary-image]');
            if (img) {
              const original = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
              wrap.querySelectorAll('[data-image]').forEach((swatch) =>
                swatch.addEventListener('mouseenter', () => {
                  img.removeAttribute('srcset');
                  img.src = swatch.dataset.image;
                })
              );
              wrap.addEventListener('mouseleave', () => {
                img.src = original.src;
                if (original.srcset) img.setAttribute('srcset', original.srcset);
              });
            }
          }

          const observer = new ResizeObserver(check);
          observer.observe(list);
          this.swatchObservers.push(observer);
          check();
        });
      }
    }
  );
}
